import React, { useState } from 'react';
import { BookOpen, Search, Star, Eye, ThumbsUp, ThumbsDown } from 'lucide-react';
import { KnowledgeBaseArticle } from '../../lib/supportService';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { SearchInput } from '../common/SearchInput';

interface KnowledgeBaseProps {
  articles: KnowledgeBaseArticle[];
}

export function KnowledgeBase({ articles }: KnowledgeBaseProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeBaseArticle | null>(null);

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const featuredArticles = filteredArticles.filter(article => article.is_featured);
  const regularArticles = filteredArticles.filter(article => !article.is_featured);

  const renderArticleContent = (content: string) => {
    // Simple markdown-like rendering
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return <h1 key={index} className="text-2xl font-bold mb-4">{line.substring(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-xl font-semibold mb-3 mt-6">{line.substring(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-lg font-medium mb-2 mt-4">{line.substring(4)}</h3>;
        }
        if (line.startsWith('- ')) {
          return <li key={index} className="ml-4">{line.substring(2)}</li>;
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return <p key={index} className="mb-2">{line}</p>;
      });
  };

  if (selectedArticle) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedArticle(null)}
            className="text-idecide-primary hover:text-idecide-primary-dark font-medium"
          >
            ← Back to Articles
          </button>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Eye size={16} />
            <span>{selectedArticle.view_count} views</span>
            <ThumbsUp size={16} />
            <span>{selectedArticle.helpful_count}</span>
            <ThumbsDown size={16} />
            <span>{selectedArticle.not_helpful_count}</span>
          </div>
        </div>

        <Card>
          <div className="p-6">
            <div className="mb-4">
              {selectedArticle.is_featured && (
                <Badge className="bg-yellow-100 text-yellow-800 mb-2">
                  <Star size={12} className="mr-1" />
                  Featured
                </Badge>
              )}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedArticle.title}
              </h1>
              {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedArticle.tags.map((tag, index) => (
                    <Badge key={index} className="bg-blue-100 text-blue-800 text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="prose max-w-none">
              {renderArticleContent(selectedArticle.content)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">Was this article helpful?</h4>
              <div className="flex space-x-3">
                <button className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <ThumbsUp size={16} />
                  <span>Yes</span>
                </button>
                <button className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <ThumbsDown size={16} />
                  <span>No</span>
                </button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="flex-1">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search knowledge base..."
          icon={<Search size={16} />}
        />
      </div>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Star className="text-yellow-500 mr-2" size={20} />
            Featured Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <Card key={article.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedArticle(article)}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <Badge className="bg-yellow-100 text-yellow-800">
                      <Star size={12} className="mr-1" />
                      Featured
                    </Badge>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <Eye size={12} />
                      <span>{article.view_count}</span>
                    </div>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                    {article.content.substring(0, 150)}...
                  </p>
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {article.tags.slice(0, 3).map((tag, index) => (
                        <Badge key={index} className="bg-blue-100 text-blue-800 text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All Articles */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BookOpen className="text-gray-500 mr-2" size={20} />
          All Articles
        </h2>
        
        {regularArticles.length === 0 && filteredArticles.length === 0 ? (
          <Card>
            <div className="p-8 text-center">
              <Search className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Articles Found</h3>
              <p className="text-gray-600">
                No articles match your search criteria.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {regularArticles.map((article) => (
              <Card key={article.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedArticle(article)}>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {article.content.substring(0, 200)}...
                      </p>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {article.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} className="bg-blue-100 text-blue-800 text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500 ml-4">
                      <Eye size={12} />
                      <span>{article.view_count}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 